import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, TextField, Chip } from '@mui/material';
import Layout from './Layout';
import Swal from 'sweetalert2';
import TeacherSelector from './GestioneLezioni/TeacherSelector';
import CourseSelector from './GestioneLezioni/CourseSelector';
import LezioniTable from './GestioneLezioni/LezioniTable';
import LezioneForm from './GestioneLezioni/LezioneForm';
import {
    fetchTeachers,
    fetchCoursesByTeacher,
    fetchLezioniByCorso,
    addLezioneToCorso
} from './GestioneLezioni/api';

const GestioneLezioni = () => {
    const [teachers, setTeachers] = useState([]);
    const [courses, setCourses] = useState([]);
    const [lezioni, setLezioni] = useState([]);

    const [selectedTeacher, setSelectedTeacher] = useState('');
    const [selectedCourse, setSelectedCourse] = useState('');
    const [progrLezione, setProgrLezione] = useState('');

    const [loadingTeachers, setLoadingTeachers] = useState(true);
    const [loadingCourses, setLoadingCourses] = useState(false);
    const [loadingLezioni, setLoadingLezioni] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Carica gli insegnanti al mount
    useEffect(() => {
        const loadTeachers = async () => {
            try {
                const data = await fetchTeachers();
                setTeachers(data);
            } catch (error) {
                Swal.fire('Errore', 'Impossibile recuperare la lista degli insegnanti', 'error');
            } finally {
                setLoadingTeachers(false);
            }
        };
        loadTeachers();
    }, []);

    // Carica i corsi quando cambia l'insegnante selezionato
    useEffect(() => {
        const loadCourses = async () => {
            if (!selectedTeacher) {
                setCourses([]);
                setSelectedCourse('');
                setLezioni([]);
                return;
            }

            setLoadingCourses(true);
            try {
                const data = await fetchCoursesByTeacher(selectedTeacher);
                console.debug('Loaded courses for teacher', selectedTeacher, data && data.length ? data.map(c=>c.ID) : data);
                setCourses(data);
                setSelectedCourse('');
                setLezioni([]);
            } catch (error) {
                Swal.fire('Errore', 'Impossibile recuperare la lista dei corsi per questo insegnante', 'error');
            } finally {
                setLoadingCourses(false);
            }
        };
        loadCourses();
    }, [selectedTeacher]);

    // Carica le lezioni quando cambia il corso selezionato
    useEffect(() => {
        const loadLezioni = async () => {
            if (!selectedCourse) {
                setLezioni([]);
                return;
            }

            setLoadingLezioni(true);
            try {
                const data = await fetchLezioniByCorso(selectedCourse);
                setLezioni(data);
            } catch (error) {
                console.error('Errore nel recupero delle lezioni', error);
                setLezioni([]);
            } finally {
                setLoadingLezioni(false);
            }
        };
        loadLezioni();
    }, [selectedCourse]);

    const handleAddLezione = async () => {
        if (!selectedTeacher || !selectedCourse || !progrLezione) {
            Swal.fire('Attenzione', 'Seleziona un insegnante, un corso e inserisci il progr_lezione', 'warning');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await addLezioneToCorso(selectedCourse, progrLezione);

            if (response.success || response.status === 200) {
                Swal.fire('Successo', 'Lezione aggiunta con successo', 'success');
                setProgrLezione('');
                // Ricarica le lezioni
                const data = await fetchLezioniByCorso(selectedCourse);
                setLezioni(data);
            } else {
                Swal.fire('Errore', response.error || 'Errore durante l\'aggiunta della lezione', 'error');
            }
        } catch (error) {
            console.error('Errore durante l\'aggiunta:', error);
            Swal.fire('Errore', 'Errore di comunicazione col server', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Layout>
            <Box className="dashboard-header" sx={{ mb: 4 }}>
                <Typography className="dashboard-title" sx={{ fontSize: '1.6rem !important' }}>
                    Gestione Lezioni
                </Typography>
                <Typography variant="body2" className="dashboard-subtitle">
                    Amministrazione: aggiungi associazioni presenze_lezioni per i corsi.
                </Typography>
            </Box>

            <Card sx={{ maxWidth: 600, mx: 'auto', p: 2, background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)' }}>
                <CardContent>
                    {/* Selezionatore Insegnante */}
                    <TeacherSelector
                        teachers={teachers}
                        selectedTeacher={selectedTeacher}
                        loading={loadingTeachers}
                        onTeacherChange={setSelectedTeacher}
                    />

                    {/* Selezionatore Corso */}
                    <CourseSelector
                        courses={courses}
                        selectedCourse={selectedCourse}
                        loading={loadingCourses}
                        disabled={!selectedTeacher}
                        onCourseChange={(val) => { console.debug('CourseSelector onCourseChange', val); setSelectedCourse(val); }}
                    />

                    {/* Controllo visibile: mostra l'id del corso selezionato */}
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            label="ID Corso selezionato"
                            value={selectedCourse ?? ''}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            helperText={selectedCourse ? `Parsed: ${Number(selectedCourse)}` : 'Nessun corso selezionato'}
                        />
                        {selectedCourse !== '' && !Number.isFinite(Number(selectedCourse)) && (
                            <Chip label={`ID non valido: ${selectedCourse}`} color="error" sx={{ mt: 1 }} />
                        )}
                    </Box>

                    {/* Form per aggiungere lezione */}
                    <LezioneForm
                        progrLezione={progrLezione}
                        onProgrLezioneChange={setProgrLezione}
                        onAddLezione={handleAddLezione}
                        disabled={!selectedCourse}
                        submitting={isSubmitting}
                    />

                    {/* Tabella Lezioni */}
                    {selectedCourse && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Lezioni del corso
                            </Typography>
                            <LezioniTable
                                lezioni={lezioni}
                                loading={loadingLezioni}
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Layout>
    );
};

export default GestioneLezioni;
